import cn from 'classnames';
import PropTypes from 'prop-types';

import { ADD_NEW_PAYMENT } from 'constants/checkoutButtons';
import { CHECKOUT_STEP_MAP, PAYMENT_STEP } from 'constants/checkoutFlow';
import { CC_NUM_FIELD_ID, PAYMENT_FIELDS } from 'constants/formFields';

import BackButton from 'components/checkout/caap/BackButton';
import CheckboxInput from 'components/checkout/caap/CheckboxInput';
import ContentBox from 'components/checkout/caap/ContentBox';
import ErrorList from 'components/checkout/caap/ErrorList';
import GiftCardContainer from 'components/checkout/caap/GiftCardContainer';
import { Column, Row, RowSpan } from 'components/checkout/caap/Layout';
import ModalOverlay from 'components/checkout/caap/ModalOverlay';
import CCIVRPayment from 'components/checkout/caap/payment/CCIVRPayment';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import { CreditCardExpirationMonth, CreditCardExpirationYear } from 'components/common/CreditCardExpirationField';
import { TextField } from 'components/Mdl';
import { getFieldFocusEvents } from 'helpers/CheckoutUtils';
import {
  itemBox as styleItemBox,
  itemContainer as styleItemContainer,
  solo as styleItemSolo
} from 'styles/components/checkout/caap/contentBox.scss';
import css from 'styles/components/checkout/caap/payment/newPaymentTypeModal.scss';

const textfieldFocusEvents = getFieldFocusEvents(css.hasFormElementWithFocus);
const selectFocusEvents = getFieldFocusEvents(css.expDateFieldFocus);

const hasError = (key, errors) => errors[key] && errors[key].length;

const wrapperStyles = {
  [css.formInputWrapper]: true
};

const NewPaymentTypeModal = (componentParams, { testId }) => {
  const {
    onCloseCCIVR,
    isNewPrimary,
    onMakeNewPaymentPrimaryClick,
    onCloseModalOverlayClick,
    pageHistory,
    constraintViolations,
    onGiftCardVerify,
    onGiftCardChange,
    giftCards = {},
    hasSavedPayments,
    onToggleGiftCardModalClick,
    useNameOnAccount = false,
    onToggleUseNameOnAccountClick,
    isFetchingCheckoutData,
    onAddNewPaymentClick,
    payment,
    onPaymentTypeFieldChange,
    showAsModal,
    showCCIVRPayment
  } = componentParams;

  const containerStyle = cn(css.wrapper, { [css.inlineFormContainer]: !showAsModal });

  if (!payment) {
    return null;
  }

  const errors = payment.errors || {};
  let errorList = [];
  Object.keys(errors).forEach(key => {
    if (errors[key]) {
      errorList = [...errorList, ...errors[key]];
    }
  });

  const nameOnCardWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.first]: true,
      [css.error]: hasError(PAYMENT_FIELDS.NAME_ON_CARD.fieldName, errors)
    }
  );

  const cardNumberWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.last]: true,
      [css.error]: hasError(PAYMENT_FIELDS.CC.fieldName, errors)
    }
  );

  const expDateWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.solo]: true,
      [css.error]: hasError(PAYMENT_FIELDS.CC_EXPIRATION.fieldName, errors)
    }
  );

  if (showCCIVRPayment) {
    return (
      <CCIVRPayment onCloseModalOverlayClick={onCloseCCIVR} />
    );
  }

  return (
    <div className={containerStyle}>
      <ModalOverlay onCloseModalOverlayClick={onCloseModalOverlayClick} showAsModal={showAsModal}>
        {
          showAsModal
            ? <div className={css.modalTitleContainer}>
              {
                payment.paymentInstrumentId
                  ? 'Edit billing address'
                  : 'Add a debit or credit card'
              }
              <button
                type="button"
                className={css.closeButton}
                data-test-id={testId('closeModal')}
                onClick={onCloseModalOverlayClick}
                onKeyPress={onCloseModalOverlayClick}
                role="link"
                tabIndex="0"
                title="Close Modal"></button>
            </div>
            : <div className={css.modalTitleContainer}>Add a debit or credit card</div>
        }

        {
          pageHistory && pageHistory.includes('pay')
            ? <BackButton href={CHECKOUT_STEP_MAP[PAYMENT_STEP]} />
            : null
        }

        <div className={css.showOnlySmallScreen}><h1 className={css.mediumSize}>Enter a payment method</h1></div>

        <Row extraClass={css.form} isVerticalForm={true}>
          <div className={styleItemContainer}>
            <form method="post" onSubmit={onAddNewPaymentClick}>
              <Row extraClass={`${styleItemBox} ${styleItemSolo}`}>
                <div className={css.formBody}>
                  <Row>
                    <RowSpan span="12">
                      <ErrorList
                        errorListKey="new-payment"
                        errorList={errorList}
                        errorHeading="There was a problem" />
                      <h2 className={`${css.showOnlySmallScreen} ${css.mediumSize}`}>Enter your card information</h2>
                      <SmallScreenOnly>
                        <CheckboxInput
                          isChecked={useNameOnAccount}
                          onChange={onToggleUseNameOnAccountClick}
                          name="useNameOnAccount"
                          dataTestId="useNameOnAccount">
                          Use name on account
                        </CheckboxInput>
                      </SmallScreenOnly>
                    </RowSpan>
                  </Row>

                  <Row>
                    <div className={css.newPaymentLeft}>
                      <Row>
                        <RowSpan mobileSpan="0" desktopSpan="5">
                          <div className={css.formLabelContainer}>
                            <label htmlFor={PAYMENT_FIELDS.NAME_ON_CARD.fieldName}>Name on card:</label>
                          </div>
                        </RowSpan>
                        <RowSpan mobileSpan="12" desktopSpan="7">
                          <div { ...textfieldFocusEvents } className={nameOnCardWrapperStyles}>
                            <TextField
                              autoComplete="cc-name"
                              onChange={onPaymentTypeFieldChange}
                              value={payment[PAYMENT_FIELDS.NAME_ON_CARD.fieldName]}
                              maxLength={PAYMENT_FIELDS.NAME_ON_CARD.maxLength}
                              name={PAYMENT_FIELDS.NAME_ON_CARD.fieldName}
                              id={PAYMENT_FIELDS.NAME_ON_CARD.fieldName}
                              placeholder="Name on Card"
                              data-test-id={testId('nameOnCard')} />
                          </div>
                        </RowSpan>
                      </Row>

                      <Row>
                        <RowSpan mobileSpan="0" desktopSpan="5">
                          <div className={css.formLabelContainer}>
                            <label htmlFor={PAYMENT_FIELDS.CC.fieldName}>Card number:</label>
                          </div>
                        </RowSpan>
                        <RowSpan mobileSpan="12" desktopSpan="7">
                          <div { ...textfieldFocusEvents } className={cardNumberWrapperStyles}>
                            <TextField
                              autoComplete="cc-number"
                              id={CC_NUM_FIELD_ID}
                              onChange={onPaymentTypeFieldChange}
                              value={payment[PAYMENT_FIELDS.CC.fieldName]}
                              maxLength={PAYMENT_FIELDS.CC.maxLength}
                              name={PAYMENT_FIELDS.CC.fieldName}
                              id={PAYMENT_FIELDS.CC.fieldName}
                              placeholder="Card Number"
                              data-test-id={testId('cardNumber')} />
                          </div>
                        </RowSpan>
                      </Row>

                      <Row mobileOnly={true}>
                        <RowSpan span="12">
                          <h2 className={`${css.expDateHeading} ${css.mediumSize}`}>Expiration Date</h2>
                        </RowSpan>
                      </Row>

                      <Row>
                        <RowSpan mobileSpan="0" desktopSpan="5">
                          <div className={css.formLabelContainer}>
                            <label htmlFor={PAYMENT_FIELDS.CC_EXPIRATION_MONTH.fieldName}>Expiration date:</label>
                          </div>
                        </RowSpan>
                        <RowSpan mobileSpan="12" desktopSpan="7">
                          <Row>
                            <RowSpan span="6">
                              <Column>
                                <div { ...selectFocusEvents } className={expDateWrapperStyles}>
                                  <CreditCardExpirationMonth
                                    value={payment[PAYMENT_FIELDS.CC_EXPIRATION_MONTH.fieldName]}
                                    onChange={onPaymentTypeFieldChange}
                                    id={PAYMENT_FIELDS.CC_EXPIRATION_MONTH.fieldName}
                                    name={PAYMENT_FIELDS.CC_EXPIRATION_MONTH.fieldName} />
                                </div>
                              </Column>
                            </RowSpan>
                            <RowSpan span="6">
                              <Column isLast={true}>
                                <div { ...selectFocusEvents } className={expDateWrapperStyles}>
                                  <CreditCardExpirationYear
                                    value={payment[PAYMENT_FIELDS.CC_EXPIRATION_YEAR.fieldName]}
                                    onChange={onPaymentTypeFieldChange}
                                    id={PAYMENT_FIELDS.CC_EXPIRATION_YEAR.fieldName}
                                    name={PAYMENT_FIELDS.CC_EXPIRATION_YEAR.fieldName} />
                                </div>
                              </Column>
                            </RowSpan>
                          </Row>
                        </RowSpan>
                      </Row>

                      <Row>
                        <RowSpan mobileSpan="0" desktopSpan="5">&nbsp;</RowSpan>
                        <RowSpan mobileSpan="12" desktopSpan="6">
                          <CheckboxInput
                            isChecked={isNewPrimary}
                            onChange={onMakeNewPaymentPrimaryClick}
                            name="makeNewPaymentPrimary"
                            dataTestId="makeNewPaymentPrimary">
                            Use as my default payment
                          </CheckboxInput>
                        </RowSpan>
                      </Row>
                    </div>

                    <div className={css.colRight}>
                      <Row desktopOnly={true}>
                        <RowSpan span="12">
                          <Column>
                            <p>We accept all major credit and debit cards.</p>
                            <img
                              alt="Accepted Cards"
                              src="https://images-na.ssl-images-amazon.com/images/G/01/Zappos/checkout/credit-card-icons._CB292509840_.gif" />
                          </Column>
                        </RowSpan>
                      </Row>
                    </div>
                  </Row>
                </div>

                <Row>
                  <div className={css.continueButtonContainer}>
                    <button
                      className={css.addCardButton}
                      data-test-id="addNewPaymentButton"
                      disabled={isFetchingCheckoutData}
                      id={ADD_NEW_PAYMENT}
                      onClick={onAddNewPaymentClick}
                      type="submit">
                      Add your card
                    </button>
                    {
                      hasSavedPayments && <button
                        className={css.checkoutSecondaryButton}
                        data-test-id={testId('closeButton')}
                        disabled={isFetchingCheckoutData}
                        onClick={onCloseModalOverlayClick}
                        type="button">
                        Close
                      </button>
                    }
                  </div>
                </Row>

              </Row>
            </form>
          </div>

          <ContentBox
            showOnlySmallScreen={true}
            isNonRadio={true}
            isSolo={true}>
            <GiftCardContainer
              constraintViolations={constraintViolations}
              isFetchingCheckoutData={isFetchingCheckoutData}
              onGiftCardVerify={onGiftCardVerify}
              onGiftCardChange={onGiftCardChange}
              code={giftCards.code || ''}
              showGiftCardModal={giftCards.showModal}
              onToggleGiftCardModalClick={onToggleGiftCardModalClick}
            />
          </ContentBox>
        </Row>
      </ModalOverlay>
    </div>
  );
};

NewPaymentTypeModal.contextTypes = {
  testId: PropTypes.func
};

export default NewPaymentTypeModal;
